import React, { MouseEventHandler, useCallback, useEffect, useRef, useState } from "react"

import Check from "@untitled-ui/icons-react/build/esm/Check"
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown"
import Plus from "@untitled-ui/icons-react/build/esm/Plus"
import SearchLg from "@untitled-ui/icons-react/build/esm/SearchLg"

import { ListAPIResponse } from "../../models/ListAPIResponse"

import { classNames, cn } from "../../utils/classNames"
import Tooltip from "../ui/Tooltip"
import InputWrapper from "./wrapper/InputWrapper"
import { SerializedError } from "@reduxjs/toolkit"
import { FetchBaseQueryError } from "@reduxjs/toolkit/query"
import { SpinnerCircularFixed } from "spinners-react"
import XCircle from "@untitled-ui/icons-react/build/esm/XCircle"
import Button from "../button/Button"
import { Virtuoso } from "react-virtuoso"
import { DropdownOption } from "../../models/DropdownOption"
import { StatusEnum } from "../../enum/StatusEnum"
import { useDebounceHook } from "../../lib/hooks/useDebounceHook"

interface props<T> {
    label?: string
    value: string | undefined
    error?: string
    subtitle?: string
    queryHook: (search: string, page: number) => {
        data: ListAPIResponse<T> | undefined
        isLoading: boolean
        isFetching: boolean
        error: FetchBaseQueryError | SerializedError | undefined
    }
    handleOnChange: (value: T) => void
    handleOnClear?: () => void
    getDisplayValue: (object: T) => string
    customRenderOption?: (object: T) => JSX.Element
    searchPlaceholder?: string
    icon?: React.ReactNode
    enableCreateNew?: boolean
    onCreateNew?: (searchText?: string) => void
    showLabel?: boolean
    addDefaultOption?: boolean
    getDefaultValue?: (val: string) => string
    onDefaultOptionSelect?: (val: string) => void
    disable?: boolean
    customOptions?: T[]
    required?: boolean
    dropDownValuePlaceholder?: string
}

const renderCreateNewOption = (onCreateNewClick: MouseEventHandler<HTMLDivElement>) => (
    <div
        key="Create_new"
        className=" h-10 px-2.5 hover:bg-background_primary cursor-pointer flex items-center"
        onClick={onCreateNewClick}
    >
        <Plus className="w-5 h-5 text-secondary_color" />
        <p className="font-medium text-md p-2 text-secondary_color hover:bg-background_primary"> Create New</p>
    </div>
)

const renderNoData = () => (
    <div key="No_data" className="py-2 px-2.5 hover:bg-background_primary cursor-pointer flex gap-2">
        <p className="font-normal text-base text-text_primary_color italic">No data</p>
    </div>
)

const renderEmptyListContent = (onCreateNewClick: MouseEventHandler<HTMLDivElement>, enableCreateNew?: boolean) => {
    return enableCreateNew ? renderCreateNewOption(onCreateNewClick) : renderNoData()
}

const renderSearchingState = () => (
    <div key="loading" className="py-2 px-2.5 hover:bg-background_primary cursor-pointer flex gap-2">
        <SpinnerCircularFixed
            size={20}
            thickness={150}
            speed={100}
            color="rgb(14 10 77 / var(--tw-bg-opacity))"
            secondaryColor="rgba(0,0,0,0.44)"
        />
        <p className="font-normal text-base text-text_secondary_color italic">Searching</p>
    </div>
)

const renderEmptySearchMessage = () => (
    <div key="empty" className="py-2 px-2.5 hover:bg-background_primary cursor-pointer flex gap-2">
        <p className="font-normal text-base text-text_secondary_color italic">Search to get results</p>
    </div>
)

// Footer component for Virtuoso
const Footer = () => {
    return (
        <div className="p-4 flex justify-center">
            <SpinnerCircularFixed
                size={20}
                thickness={150}
                speed={100}
                color="rgb(14 10 77 / var(--tw-bg-opacity))"
                secondaryColor="rgba(0,0,0,0.44)"
            />
            <p className="ml-2 font-normal text-base text-text_secondary_color">Loading...</p>
        </div>
    )
}

const DynamicDropdown = <T,>(props: props<T>) => {
    const { value = "" } = props
    const dropdownRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const dropdownContentRef = useRef<HTMLUListElement | null>(null)

    // State management
    const [currentValue, setCurrentValue] = useState<string>("")
    const [openDropdown, setOpenDropdown] = useState<boolean>(false)
    const [dropdownOptions, setDropdownOptions] = useState<T[]>([])
    const [dropdownPosition, setDropdownPosition] = useState<"above" | "below">("below")
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [hasNextPage, setHasNextPage] = useState<boolean>(false)
    const [recentlySearched, setRecentlySearched] = useState(false)
    const [virtuosoContainerHeight, setVirtuosoContainerHeight] = useState(40)

    const loadingRef = useRef<boolean>(false)

    // Hooks
    const debouncedCurrentValue = useDebounceHook(currentValue, 200)
    const { data: apiRes, isLoading, isFetching } = props.queryHook(debouncedCurrentValue, currentPage)

    useEffect(() => {
        if (apiRes?.data) {
            // setDropdownOptions(apiRes?.data)

            if (currentPage === 1) {
                // Replace options on first page
                setDropdownOptions(apiRes?.data)
            } else {
                // Append options on subsequent pages
                setDropdownOptions((prev) => [...prev, ...apiRes?.data])
            }

            // Update pagination status
            if (apiRes?.meta) {
                setHasNextPage(apiRes.meta.hasNextPage)
            }

            // Reset loading more state
            loadingRef.current = false
        }
    }, [apiRes?.data, apiRes?.meta])

    // Reset search on dropdown close
    useEffect(() => {
        if (!openDropdown) {
            setCurrentValue("")
            setCurrentPage(1)
        }
    }, [openDropdown])

    useEffect(() => {
        if (inputRef.current && openDropdown) {
            inputRef.current.focus()
        }

        const handleClickOutside = (event: any) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) && openDropdown) {
                setOpenDropdown(false)
            }
        }

        document.addEventListener("mousedown", handleClickOutside)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [openDropdown])

    // Calculate dropdown position
    useEffect(() => {
        if (openDropdown && dropdownRef.current && dropdownContentRef.current) {
            const dropdownRect = dropdownRef.current.getBoundingClientRect()
            const viewportHeight = window.innerHeight
            const dropdownContentRect = dropdownContentRef.current.getBoundingClientRect()

            setDropdownPosition(viewportHeight - dropdownRect.bottom < dropdownContentRect.height ? "above" : "below")
        }
    }, [openDropdown])

    // Event handlers
    const toggleDropdown = () => {
        if (!props.disable) {
            setOpenDropdown(!openDropdown)
        }
    }

    const onSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {

        setRecentlySearched(true)
        setHasNextPage(false)
        setCurrentPage(1)
        setCurrentValue(e.target.value)

        // This is for a small delay as soon as we search when at the bottom of the list, so duplicate api's will not trigger
        setTimeout(() => {
            setRecentlySearched(false)
        }, 500)
    }

    const onSelect = (option: T) => {
        setOpenDropdown(false)
        setDropdownOptions([])
        setCurrentPage(1)
        props.handleOnChange(option)
    }

    const onDefaultSelect = (text: string) => {
        if (props.onDefaultOptionSelect) {
            setOpenDropdown(false)
            setDropdownOptions([])
            setCurrentPage(1)
            props.onDefaultOptionSelect(text)
        }
    }

    const onCreateNewClick = () => {
        setOpenDropdown(false)
        setDropdownOptions([])
        setCurrentPage(1)
        if (props.onCreateNew) {
            props.onCreateNew(debouncedCurrentValue)
        }
    }

    const handleKeyboardEvents: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
        if (props.disable) return

        if (e.key === "Enter") {
            e.preventDefault()
            toggleDropdown()
        }
    }

    const handleDropdownKeyDown = (e: React.KeyboardEvent) => {
        if (!openDropdown) return

        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            const index = dropdownOptions.findIndex((option) => props.getDisplayValue(option) === props.value)

            if (e.key === "ArrowDown" && index < dropdownOptions.length - 1) {
                props.handleOnChange(dropdownOptions[index + 1])
            } else if (e.key === "ArrowUp" && index > 0) {
                props.handleOnChange(dropdownOptions[index - 1])
            }
        }

         if (e.key === "Enter") {
             e.preventDefault()

             if (dropdownOptions.length > 0) {
                 // If there's already a selection, keep it, otherwise select first option
                 const currentIndex = dropdownOptions.findIndex(
                     (option) => props.getDisplayValue(option) === props.value
                 )

                 if (currentIndex === -1) {
                     props.handleOnChange(dropdownOptions[0])
                 }
             }

             setOpenDropdown(false)
         }
    }

    const renderDefaultOption = () => (
        <div
            onKeyDown={handleKeyboardEvents}
            key={-1}
            className={classNames(
                props.getDefaultValue && props.getDefaultValue(currentValue) === props.value
                    ? "bg-background_primary"
                    : "",
                "py-2 px-2.5 hover:bg-background_primary cursor-pointer flex items-center justify-between"
            )}
            onClick={() => onDefaultSelect(currentValue)}
        >
            {props.getDefaultValue && (
                <p className="font-normal text-base text-text_primary_color">{props.getDefaultValue(currentValue)}</p>
            )}
            {props.getDefaultValue && props.getDefaultValue(currentValue) === props.value && (
                <Check className="w-4 h-4 text-primary_color" />
            )}
        </div>
    )

    const renderOption = (option: T, index: number, isCustomOption = false) => (
        <li
            key={index}
            className={classNames(
                props.getDisplayValue(option) === props.value ? "bg-background_primary" : "",
                "py-2 px-2.5 hover:bg-background_primary cursor-pointer flex items-center justify-between"
            )}
            onClick={() => onSelect(option)}
        >
            {props.customRenderOption ? (
                props.customRenderOption(option)
            ) : (
                <p className="font-normal text-base text-text_primary_color">{props.getDisplayValue(option)}</p>
            )}
            {props.getDisplayValue(option) === props.value && <Check className="w-4 h-4 text-primary_color" />}
        </li>
    )

    // Virtuoso related functions
    const loadMore = useCallback(() => {

        if (hasNextPage && !isLoading && !isFetching && !loadingRef.current && !recentlySearched) {
            loadingRef.current = true

            // Add slight delay to prevent rapid successive calls
            setTimeout(() => {
                setCurrentPage((prevPage) => prevPage + 1)
            }, 300)
        }
    }, [hasNextPage, isLoading, isFetching, recentlySearched])

    // Prepare data for Virtuoso
    const prepareVirtuosoData = () => {
        // Special cases for empty states
        if (dropdownOptions.length === 0 && currentValue.length === 0) {
            return [{ type: "empty-message" }]
        }

        if (dropdownOptions.length === 0 && currentValue.length > 0 && (isLoading || isFetching)) {
            return [{ type: "loading" }]
        }

        if (dropdownOptions.length === 0 && currentValue.length > 0 && !isLoading && !isFetching) {
            return [{ type: "empty-list", enableCreateNew: props.enableCreateNew }]
        }

        // Regular options
        // @ts-ignore
        let items = []

        // Add custom options if applicable
        if (
            props.customOptions &&
            props.customOptions.length > 0 &&
            dropdownOptions.length === 0 &&
            currentValue.length === 0
        ) {
            items = props.customOptions.map((option) => ({ type: "custom-option", option }))
        }

        // Add dropdown options
        const optionsWithType = dropdownOptions
            .map((option, index) => {
                const result = []

                // Add default option if needed
                if (props.addDefaultOption && index === 0) {
                    result.push({ type: "default-option" })
                }

                result.push({ type: "option", option })
                return result
            })
            .flat()

        // @ts-ignore
        return [...items, ...optionsWithType]
    }

    // Virtuoso item content renderer
    const itemContent = (index: number, item: any) => {
        switch (item.type) {
            case "empty-message":
                return renderEmptySearchMessage()
            case "loading":
                return renderSearchingState()
            case "empty-list":
                return renderEmptyListContent(onCreateNewClick, item.enableCreateNew)
            case "default-option":
                return renderDefaultOption()
            case "custom-option":
            case "option":
                return renderOption(item.option, index)
            default:
                return null
        }
    }

    return (
        <InputWrapper
            // required={props.required}
            ref={dropdownRef}
            showLabel={props.showLabel}
            label={props.label}
            subtitle={props.subtitle}
            error={props.error}
        >
            <div
                onKeyDown={handleKeyboardEvents}
                onClick={toggleDropdown}
                tabIndex={0}
                className={classNames(
                    props.error ? "border-primary_error" : "border-border_primary",
                    props.disable ? "cursor-not-allowed opacity-50 !bg-background_secondary" : "cursor-pointer",
                    !props.disable
                        ? props.error
                            ? "focus:border-primary_error"
                            : " border-border_primary focus:border-primary_color"
                        : "",
                    "py-3 px-3 rounded-lg border bg-white relative group focus:outline-none focus:border-primary_color"
                )}
            >
                <div className="flex items-center justify-between w-full h-full">
                    {!props.value?.length ? (
                        <p className="font-normal text-base text-[#9CA3AF] truncate">
                            {props.dropDownValuePlaceholder}
                        </p>
                    ) : (
                        <p
                            className={cn(
                                " w-[90%] font-normal text-base text-text_primary_color truncate",
                                props.handleOnClear && "group-hover:w-5/6"
                            )}
                        >
                            <Tooltip content={props.value} side="top">
                                {props.value}
                            </Tooltip>
                        </p>
                    )}
                    <div>
                        <ChevronDown className="w-5 h-5 text-blue_1" />
                    </div>
                </div>
                {props.handleOnClear && (
                    <Button
                        onClick={props.handleOnClear}
                        className="!p-0 absolute right-8 top-1/2 -translate-y-1/2 hidden group-hover:block"
                        buttonType={5}
                        icon={
                            <XCircle className="w-5 h-5 text-text_secondary_color/80 hover:text-text_secondary_color" />
                        }
                    />
                )}
            </div>
            {openDropdown && (
                <ul
                    ref={dropdownContentRef}
                    className={classNames(
                        dropdownPosition === "below" ? "top-[100%]" : "bottom-[100%]",
                        "absolute left-0 w-full max-h-80 overflow-auto bg-white rounded-lg border border-border_primary z-10"
                    )}
                >
                    <li className="flex items-center gap-4 border-b border-border_secondary p-2">
                        <SearchLg className="w-5 h-5 text-blue_1" />
                        <input
                            onKeyDown={handleDropdownKeyDown}
                            ref={inputRef}
                            type="text"
                            placeholder={props.searchPlaceholder || "Search"}
                            className="w-full outline-none border-none focus:outline-none focus:border-none"
                            value={currentValue}
                            onChange={onSearchTextChange}
                        />
                    </li>
                    {/* {renderDropdownContent()} */}
                    <Virtuoso
                        style={{ maxHeight: "200px", height: `${virtuosoContainerHeight}px`}}
                        totalListHeightChanged={(height) => {
                            console.log(height)
                            setVirtuosoContainerHeight(height)
                        }}
                        data={prepareVirtuosoData()}
                        endReached={loadMore}
                        // increaseViewportBy={200}
                        itemContent={itemContent}
                        components={{ Footer: hasNextPage ? Footer : undefined }}
                        overscan={2}
                    />
                </ul>
            )}
        </InputWrapper>
    )
}

export default DynamicDropdown
